/**
 * Bloco "Carga na marcenaria" do painel do ambiente: a exceção marcada pelo
 * PCP na Capacidade da Fábrica (leve, normal ou pesado), o motivo, quem
 * marcou e as marcas anteriores.
 *
 * Só leitura (decisão 9: quem marca é o PCP, na aba Exceções da Capacidade
 * da Fábrica). Aparece para quem pode ver a Capacidade da Fábrica; para os
 * demais o painel continua igual.
 */
import React from "react";
import styled from "styled-components";

import useExcecoesCapacidade from "../../hooks/useExcecoesCapacidade";
import { chaveAmbiente } from "../../pages/MapaProducao/dados/chaves";
import { textoDaMarca, textoDoPeso, textoQuemQuando } from "../../pages/CapacidadeFabrica/apresentacaoExcecoes";
import { BotaoTexto } from "../../pages/CapacidadeFabrica/styles";
import { SectionTitle } from "../AmbienteDetalhesDrawer/styles";

function ErroDaLeitura({ onTentarDeNovo }) {
  return (
    <section aria-label="Carga na marcenaria">
      <SectionTitle>Carga na marcenaria</SectionTitle>
      <Corpo role="note">
        <Nota>Não foi possível ler as marcas do PCP.</Nota>
        <BotaoTexto type="button" onClick={onTentarDeNovo}>
          Tentar de novo
        </BotaoTexto>
      </Corpo>
    </section>
  );
}

/** `row` = linha CRUA da planilha (a chave sai dela). */
export default function CargaNaMarcenaria({ row }) {
  const { excecoes, carregado, permitido, erro, recarregar } = useExcecoesCapacidade();
  const chave = chaveAmbiente(row);
  if (!permitido || !carregado || !chave) return null;
  // Sem a leitura não dá para dizer "sem marca": seria afirmar o que não se sabe.
  if (erro) return <ErroDaLeitura onTentarDeNovo={recarregar} />;
  const marca = excecoes[chave] || null;
  // Mais recente primeiro: o backend manda a mais antiga primeiro.
  const anteriores = [...(marca?.anteriores || [])].reverse();

  return (
    <section aria-label="Carga na marcenaria">
      <SectionTitle>Carga na marcenaria</SectionTitle>
      <Corpo>
        {marca ? (
          <>
            <Atual>
              <strong>{textoDaMarca(marca)}</strong>
              <span>{textoDoPeso(marca.classe)} na leitura com as exceções</span>
            </Atual>
            {marca.observacao ? <Observacao>{marca.observacao}</Observacao> : null}
            <Nota>Marcado {textoQuemQuando(marca)}.</Nota>
            {anteriores.length > 0 ? (
              <Historico aria-label="Marcas anteriores">
                {anteriores.map((anterior) => (
                  <li key={`${anterior.classe}-${anterior.marcado_em}`}>
                    <span>{textoDaMarca(anterior)}</span>
                    <span className="quando">{textoQuemQuando(anterior)}</span>
                  </li>
                ))}
              </Historico>
            ) : null}
          </>
        ) : (
          <Nota>Sem marca do PCP: conta como 1 ambiente, como qualquer outro.</Nota>
        )}
      </Corpo>
    </section>
  );
}

const Corpo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 10px;
  background: ${({ theme }) => theme.surface};
`;

const Atual = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  column-gap: 8px;
  row-gap: 2px;
  min-width: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.textSecondary};

  strong {
    font-weight: 650;
    color: ${({ theme }) => theme.textPrimary};
  }
`;

const Observacao = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.45;
  color: ${({ theme }) => theme.textPrimary};
  overflow-wrap: anywhere;
`;

const Nota = styled.p`
  margin: 0;
  font-size: var(--text-min, 12px);
  line-height: 1.45;
  color: ${({ theme }) => theme.textTertiary};
`;

const Historico = styled.ul`
  list-style: none;
  margin: 4px 0 0;
  padding: 6px 0 0;
  border-top: 1px dashed ${({ theme }) => theme.border};
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;

  li {
    display: flex;
    flex-wrap: wrap;
    column-gap: 8px;
    font-size: var(--text-min, 12px);
    color: ${({ theme }) => theme.textSecondary};
  }

  .quando {
    color: ${({ theme }) => theme.textTertiary};
  }
`;
